import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import api from '../lib/api';
import { toast } from 'sonner';
import { PageHeader } from '../components/common/PageHeader';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Switch } from '../components/ui/switch';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { useConfirm } from '../context/ConfirmContext';
import { Plus, Pencil, Trash2, Ban, KeyRound } from 'lucide-react';

const ALL_PERMS = ['dashboard', 'tables', 'menu', 'orders', 'kds', 'payments', 'inventory', 'reports', 'users', 'settings'];
const ROLE_KEYS = ['admin', 'manager', 'cashier', 'waiter'];

export default function UsersPage() {
  const { t } = useTranslation();
  const [users, setUsers] = useState([]);
  const [dialog, setDialog] = useState(false);
  const [editing, setEditing] = useState(null);
  const blank = { username: '', name: '', password: '', role: 'waiter', active: true, permissions: ['tables', 'orders'], pin: '', passe_partout: false };
  const [f, setF] = useState(blank);
  const [pinPrompt, setPinPrompt] = useState(false);
  const [pinVal, setPinVal] = useState('');
  const [pinBusy, setPinBusy] = useState(false);
  const confirm = useConfirm();

  const load = async () => { const { data } = await api.get('/users'); setUsers(data); };
  useEffect(() => { load(); }, []);

  const openNew = () => { setEditing(null); setF(blank); setDialog(true); };
  const openEdit = (u) => { setEditing(u); setF({ ...u, password: '' }); setDialog(true); };

  const save = async () => {
    if (!f.username.trim() || !f.name.trim()) { toast.error(t('users.usernameNameRequired')); return; }
    if (!editing && !f.password) { toast.error(t('users.passwordRequired')); return; }
    try {
      if (editing) await api.put(`/users/${editing.id}`, f);
      else await api.post('/users', f);
      toast.success(t('users.userSaved')); setDialog(false); load();
    } catch (e) { toast.error(e?.response?.data?.detail || t('users.error')); }
  };
  const del = async (u) => { if (await confirm({ title: t('users.deleteConfirm', { name: u.name }), destructive: true, confirmText: t('users.delete') })) { try { await api.delete(`/users/${u.id}`); load(); } catch (e) { toast.error(e?.response?.data?.detail || t('users.error')); } } };

  const togglePerm = (p) => setF((s) => ({ ...s, permissions: s.permissions.includes(p) ? s.permissions.filter((x) => x !== p) : [...s.permissions, p] }));

  // Permesso "Può stornare": attivarlo richiede il PIN admin; disattivarlo è libero.
  const requestToggleVoid = (checked) => {
    if (!checked) {
      setF((s) => ({ ...s, permissions: (s.permissions || []).filter((x) => x !== 'void') }));
      return;
    }
    setPinVal(''); setPinPrompt(true);
  };
  const confirmVoidPin = async () => {
    if (!pinVal.trim()) return;
    setPinBusy(true);
    try {
      await api.post('/auth/verify-pin', { pin: pinVal.trim() });
      setF((s) => ({ ...s, permissions: Array.from(new Set([...(s.permissions || []), 'void'])) }));
      setPinPrompt(false);
      toast.success(t('users.voidEnabled', { defaultValue: 'Storno abilitato per questo utente' }));
    } catch (e) {
      toast.error(e?.response?.data?.detail || t('users.pinInvalid', { defaultValue: 'PIN non valido' }));
    } finally { setPinBusy(false); }
  };

  return (
    <div className="p-4 md:p-6">
      <PageHeader title={t('users.title')} subtitle={t('users.subtitle')}>
        <Button className="rounded-xl" onClick={openNew} data-testid="add-user-button"><Plus className="h-4 w-4 mr-2" /> {t('users.userBtn')}</Button>
      </PageHeader>
      <Card className="p-4">
        <Table data-testid="users-table">
          <TableHeader><TableRow><TableHead>{t('users.colName')}</TableHead><TableHead>{t('users.colOperator', { defaultValue: 'Operatore #' })}</TableHead><TableHead>{t('users.colUsername')}</TableHead><TableHead>{t('users.colRole')}</TableHead><TableHead>{t('users.colPin')}</TableHead><TableHead>{t('users.colStatus')}</TableHead><TableHead className="text-right">{t('users.colActions')}</TableHead></TableRow></TableHeader>
          <TableBody>
            {users.map((u) => (
              <TableRow key={u.id}>
                <TableCell className="font-medium">{u.name}</TableCell>
                <TableCell><span className="inline-flex items-center justify-center min-w-8 px-2 py-0.5 rounded-lg bg-primary/10 text-primary font-mono font-semibold text-sm" data-testid="user-operator-number">{u.operator_number != null ? `#${u.operator_number}` : '—'}</span></TableCell>
                <TableCell className="text-muted-foreground">{u.username}</TableCell>
                <TableCell>{t(`roles.${u.role}`)}</TableCell>
                <TableCell className="font-mono text-sm">{u.pin || '—'}</TableCell>
                <TableCell>{u.active ? <span className="text-xs px-2 py-1 rounded-full bg-pos-free/10 text-pos-free">{t('users.active')}</span> : <span className="text-xs px-2 py-1 rounded-full bg-muted text-muted-foreground">{t('users.inactive')}</span>}</TableCell>
                <TableCell className="text-right">
                  <Button variant="ghost" size="icon" onClick={() => openEdit(u)}><Pencil className="h-4 w-4" /></Button>
                  <Button variant="ghost" size="icon" className="text-destructive" onClick={() => del(u)}><Trash2 className="h-4 w-4" /></Button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Card>

      <Dialog open={dialog} onOpenChange={setDialog}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-auto">
          <DialogHeader><DialogTitle>{editing ? t('users.editUser') : t('users.newUser')}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <div><Label>{t('users.name')}</Label><Input value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} className="mt-1.5" data-testid="user-name-input" /></div>
              <div><Label>{t('users.username')}</Label><Input value={f.username} onChange={(e) => setF({ ...f, username: e.target.value })} className="mt-1.5" disabled={!!editing} data-testid="user-username-input" /></div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>{editing ? t('users.newPasswordOpt') : t('users.password')}</Label><Input type="password" value={f.password} onChange={(e) => setF({ ...f, password: e.target.value })} className="mt-1.5" data-testid="user-password-input" /></div>
              <div>
                <Label>{t('users.role')}</Label>
                <Select value={f.role} onValueChange={(v) => setF({ ...f, role: v })}>
                  <SelectTrigger className="mt-1.5" data-testid="user-role-select"><SelectValue /></SelectTrigger>
                  <SelectContent>{ROLE_KEYS.map((k) => <SelectItem key={k} value={k}>{t(`roles.${k}`)}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            </div>
            <div>
              <Label>{t('users.customPerms')}</Label>
              <div className="grid grid-cols-2 gap-2 mt-1.5">
                {ALL_PERMS.map((k) => (
                  <label key={k} className="flex items-center gap-2 text-sm h-9 px-2 rounded-lg border border-border cursor-pointer">
                    <input type="checkbox" checked={f.permissions?.includes(k)} onChange={() => togglePerm(k)} /> {t(`users.perms.${k}`)}
                  </label>
                ))}
              </div>
            </div>
            <div className="rounded-xl border border-border p-3" data-testid="user-canvoid-block">
              <div className="flex items-center justify-between gap-3">
                <div className="min-w-0">
                  <div className="text-sm font-medium flex items-center gap-1.5"><Ban className="h-4 w-4 text-destructive" /> {t('users.canVoid', { defaultValue: 'Può stornare' })}</div>
                  <div className="text-xs text-muted-foreground mt-0.5">{t('users.canVoidHint', { defaultValue: 'Consente di stornare prodotti già inviati e scegliere il motivo. Per attivarlo serve il PIN admin.' })}</div>
                </div>
                {(f.role === 'admin' || f.role === 'manager')
                  ? <span className="text-xs text-muted-foreground whitespace-nowrap">{t('users.alwaysOn', { defaultValue: 'Sempre attivo' })}</span>
                  : <Switch checked={(f.permissions || []).includes('void')} onCheckedChange={requestToggleVoid} data-testid="user-canvoid-switch" />}
              </div>
            </div>
            <div>
                <Label>{t('users.pin')}</Label>
                <Input value={f.pin || ''} inputMode="numeric"
                  onChange={(e) => setF({ ...f, pin: e.target.value.replace(/\D/g, '').slice(0, 6) })}
                  className="mt-1.5 font-mono tracking-widest" placeholder="••••••" data-testid="user-pin-input" />
                <div className="text-xs text-muted-foreground mt-1">{f.role === 'admin' ? t('users.pinHintAdmin', { defaultValue: 'PIN per accesso rapido al profilo amministratore.' }) : t('users.pinHint')}</div>
              </div>
            <label className="flex items-center gap-2"><Switch checked={f.active} onCheckedChange={(v) => setF({ ...f, active: v })} /> {t('users.accountActive')}</label>
          </div>
          <DialogFooter><Button variant="outline" onClick={() => setDialog(false)}>{t('users.cancel')}</Button><Button onClick={save} data-testid="user-save-button">{t('users.save')}</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Conferma PIN admin per abilitare lo storno */}
      <Dialog open={pinPrompt} onOpenChange={(o) => !pinBusy && setPinPrompt(o)}>
        <DialogContent className="max-w-sm" data-testid="canvoid-pin-dialog">
          <DialogHeader><DialogTitle className="flex items-center gap-2"><KeyRound className="h-5 w-5 text-primary" /> {t('users.pinConfirmTitle', { defaultValue: 'Conferma PIN admin' })}</DialogTitle></DialogHeader>
          <p className="text-sm text-muted-foreground">{t('users.pinConfirmDesc', { defaultValue: 'Inserisci il PIN di un amministratore per abilitare lo storno a questo utente.' })}</p>
          <Input autoFocus type="password" inputMode="numeric" value={pinVal}
            onChange={(e) => setPinVal(e.target.value.replace(/\D/g, '').slice(0, 6))}
            onKeyDown={(e) => { if (e.key === 'Enter') confirmVoidPin(); }}
            className="mt-2 font-mono tracking-widest text-center text-lg" placeholder="••••••" data-testid="canvoid-pin-input" />
          <DialogFooter>
            <Button variant="outline" onClick={() => setPinPrompt(false)} disabled={pinBusy}>{t('users.cancel')}</Button>
            <Button onClick={confirmVoidPin} disabled={pinBusy || !pinVal.trim()} data-testid="canvoid-pin-confirm">{pinBusy ? t('users.verifying', { defaultValue: 'Verifica…' }) : t('users.confirm', { defaultValue: 'Conferma' })}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
